// src/pages/SuperAdminDashboard.js
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../services/adminService';
import AuthContext from '../context/AuthContext';

const emptyNewUser = {
  name: '',
  email: '',
  password: '',
  cardLimit: ''
};

const SuperAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [error, setError] = useState('');
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [selectedUser, setSelectedUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersResponse, statsResponse, cardsResponse] = await Promise.all([
        adminService.getUsers(),
        adminService.getStats(),
        adminService.getCards()
      ]);

      setUsers(usersResponse.data);
      setStats(statsResponse.data);
      setRegistrationOpen(statsResponse.data.registrationOpen);
      setCards(cardsResponse.data);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError('Unable to load admin data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleRegistration = async () => {
    try {
      const nextValue = !registrationOpen;
      const response = await adminService.updateRegistration(nextValue);
      setRegistrationOpen(response.data.registrationOpen);
      setStats((prev) =>
        prev ? { ...prev, registrationOpen: response.data.registrationOpen } : prev
      );
    } catch (err) {
      console.error('Error updating registration settings:', err);
      setError('Unable to update registration settings.');
    }
  };

  const handleNewUserChange = (event) => {
    const { name, value } = event.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setError('');
    setCreatingUser(true);

    try {
      const payload = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password
      };

      if (newUser.cardLimit !== '') {
        payload.cardLimit = Number(newUser.cardLimit);
      }

      await adminService.createUser(payload);
      setNewUser(emptyNewUser);
      await fetchData();
    } catch (err) {
      console.error('Error creating user:', err);
      const message = err?.response?.data?.msg || 'Unable to create user. Please verify the data.';
      setError(message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleSelectUser = (userToEdit) => {
    setSelectedUser({
      id: userToEdit.id,
      name: userToEdit.name,
      email: userToEdit.email,
      cardLimit: userToEdit.cardLimit ?? '',
      password: ''
    });
  };

  const handleSelectedUserChange = (event) => {
    const { name, value } = event.target;
    setSelectedUser((prev) => (
      prev ? { ...prev, [name]: value } : prev
    ));
  };

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    if (!selectedUser) return;

    setSavingUser(true);
    setError('');

    try {
      const payload = {
        name: selectedUser.name,
        email: selectedUser.email
      };

      if (selectedUser.cardLimit === '') {
        payload.cardLimit = null;
      } else if (selectedUser.cardLimit !== null && selectedUser.cardLimit !== undefined) {
        payload.cardLimit = Number(selectedUser.cardLimit);
      }

      if (selectedUser.password) {
        payload.password = selectedUser.password;
      }

      await adminService.updateUser(selectedUser.id, payload);
      setSelectedUser(null);
      await fetchData();
    } catch (err) {
      console.error('Error updating user:', err);
      const message = err?.response?.data?.msg || 'Unable to update user. Please verify the data.';
      setError(message);
    } finally {
      setSavingUser(false);
    }
  };

  if (!user || user.role !== 'superadmin') {
    return <div className="p-8">Access denied.</div>;
  }

  if (loading) {
    return <div className="p-8">Loading super admin dashboard...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="bg-white shadow rounded p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Super Admin Dashboard</h1>
            <p className="text-gray-600">Manage users, cards, and platform settings.</p>
          </div>
          <button
            onClick={handleToggleRegistration}
            className={`px-4 py-2 rounded text-white font-semibold ${
              registrationOpen ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {registrationOpen ? 'Disable Public Registrations' : 'Enable Public Registrations'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white shadow rounded p-4">
            <h2 className="text-lg font-semibold text-gray-700">Total Users</h2>
            <p className="text-2xl font-bold">{stats.totalUsers}</p>
          </div>
          <div className="bg-white shadow rounded p-4">
            <h2 className="text-lg font-semibold text-gray-700">Total Cards</h2>
            <p className="text-2xl font-bold">{stats.totalCards}</p>
          </div>
          <div className="bg-white shadow rounded p-4">
            <h2 className="text-lg font-semibold text-gray-700">Registrations</h2>
            <p className="text-2xl font-bold">
              {registrationOpen ? 'Open' : 'Closed'}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Create New User</h2>
        <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreateUser}>
          <div>
            <label className="block text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={newUser.name}
              onChange={handleNewUserChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={newUser.email}
              onChange={handleNewUserChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-gray-700">Password</label>
            <input
              type="password"
              name="password"
              value={newUser.password}
              onChange={handleNewUserChange}
              required
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-gray-700">Card Limit (leave empty for unlimited)</label>
            <input
              type="number"
              min="0"
              name="cardLimit"
              value={newUser.cardLimit}
              onChange={handleNewUserChange}
              className="w-full p-2 border rounded"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={creatingUser}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded disabled:opacity-60"
            >
              {creatingUser ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Existing Users</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cards</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card Limit</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-2 whitespace-nowrap">{item.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{item.email}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{item.cardCount}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {item.cardLimit === null || item.cardLimit === undefined ? 'Unlimited' : item.cardLimit}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleSelectUser(item)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedUser && (
        <div className="bg-white shadow rounded p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">Edit User</h2>
            <button
              onClick={() => setSelectedUser(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleUpdateUser}>
            <div>
              <label className="block text-gray-700">Name</label>
              <input
                type="text"
                name="name"
                value={selectedUser.name}
                onChange={handleSelectedUserChange}
                required
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={selectedUser.email}
                onChange={handleSelectedUserChange}
                required
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700">Card Limit (leave empty for unlimited)</label>
              <input
                type="number"
                min="0"
                name="cardLimit"
                value={selectedUser.cardLimit}
                onChange={handleSelectedUserChange}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700">Reset Password (optional)</label>
              <input
                type="password"
                name="password"
                value={selectedUser.password}
                onChange={handleSelectedUserChange}
                className="w-full p-2 border rounded"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={savingUser}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded disabled:opacity-60"
              >
                {savingUser ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">All Business Cards</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Theme</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cards.map((card) => (
                <tr key={card._id}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {card.createdBy ? `${card.createdBy.name} (${card.createdBy.email})` : '—'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {card.contactInfo?.name || ''} {card.contactInfo?.surname || ''}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{card.contactInfo?.email}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{card.theme}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-right space-x-2">
                    <Link
                      to={`/edit/${card._id}`}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </Link>
                    <a
                      href={`${process.env.REACT_APP_API_URL || 'http://localhost:5050'}/api/static/${card._id}/${card._id}.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
